import { apiMemoria } from '@modules/aawz-intelligence-v2/api/apiMemoria'
import { ehCartaoDeBoasVindas } from '@modules/aawz-intelligence-v2/ui-registry/chave-da-boas-vindas'
import { onboardingConduzindo } from '@modules/aawz-intelligence-v2/ui-registry/chave-do-onboarding'
import {
  extrairApresentacao,
  extrasDaApresentacao,
} from '@modules/aawz-intelligence-v2/utils/apresentacao-do-profissional'
import { CONFIRMAR_PERFIL } from '@modules/aawz-intelligence-v2/constants/TextChat'
import { STORE_PRINCIPAL as STORE } from '@modules/aawz-intelligence-v2/api/chat-event-handlers'

/**
 * ★ O INTERRUPTOR do cartão "Guardar quem é você". Uma linha, e só ela.
 *
 * Owner, 18/09/2026: *"pode retirar por enquanto (pode ser por feature flag)
 * esse 'Guardar quem é você'"*.
 *
 * Desligado, o cartão não aparece — e nada mais muda: a proposta continua
 * sendo montada, a rota de memória continua de pé e os testes continuam
 * exercitando o caminho inteiro. Religar é trocar `false` por `true`.
 *
 * ⚠️ Há um motivo TÉCNICO para ele sair agora, além do pedido: o cartão vinha
 * preenchido com o RÓTULO do diálogo. Desde que a delegação passou a mandar
 * "Consultor: ..." / "IA: ...", o extrator leu "Consultor" como a PROFISSÃO da
 * pessoa (visto na tela pelo owner, campo "O que você faz"). Religar o cartão
 * sem resolver isso devolve o defeito junto.
 */
export const CARTAO_DE_PERFIL_LIGADO = false

// A plataforma passa a lembrar quem é a pessoa (spec-009, US-3).
//
// ★ **O único caminho daqui até a API passa por `confirmarPerfil`, e o único
// chamador dele é o `@confirmar` do cartão** — ou seja, um clique. É assim que
// o AC-3.3 ("a palavra falada nunca confirma") vira propriedade do desenho, e
// não uma lista de frases proibidas: não existe frase que grave, porque
// nenhuma frase chega à gravação.
//
// ★ A leitura acontece sobre a MENSAGEM DO USUÁRIO no store, que é o mesmo
// lugar para quem digitou e para quem falou — a transcrição entra na conversa
// pelo mesmo `enviarMensagem`. Daí o AC-3.1 ("fala ou texto valem igual") sair
// por construção, sem um ramo por origem que alguém precise lembrar de manter.
//
// ⚠️ **Mixin separado, e não mais um bloco em `view-chat.vue`**: aquele arquivo
// já está muito acima do teto de 500 linhas do repo (dívida registrada no
// CLAUDE.md do módulo), e engordá-lo mais é a decisão que ninguém quer
// defender no review seguinte.
export const viewChatMemoriaDoPerfilMixin = {
  data() {
    return {
      /** `{nome, papel, objetivo}` proposto pelo último turno, ou `null`. */
      propostaDePerfil: null,
      salvandoPerfil: false,
      erroDoPerfil: '',
      // Uma proposta por passagem pela tela: quem guardou não precisa guardar
      // de novo, e quem disse "agora não" não pode ser perguntado a cada
      // mensagem. Escopo é a TELA (não `localStorage`): recarregar volta a
      // oferecer, e a tela de Memória da IA continua sendo o lugar de editar.
      perfilJaTratadoNestaTela: false,
      // O texto que originou a proposta corrente — impede reavaliar a mesma
      // mensagem duas vezes quando a lista muda por outro motivo (o `end` do
      // turno reescreve `messages`).
      textoDaPropostaCorrente: '',
    }
  },
  computed: {
    /**
     * ⚠️ [20/09/2026] Era `STORE` fixo, e com as conversas VIVAS (#2349) isso
     * fazia a trava da apresentação vazar: `/home-ia` roda em
     * `iaConversa:<uuid>`, então este computed lia a lista de OUTRO slice.
     * O cartão de onboarding que sobrou no namespace fixo deixava
     * `onboardingConduzindo` verdadeiro em TODA conversa nova — a faixa "A
     * apresentação está conduzindo" e o composer travado sem cartão nenhum na
     * tela. Mesma armadilha (e mesmo conserto) do mixin dos conectores.
     */
    mensagensDaConversa() {
      return this.$store.getters[`${this.ns || STORE}/messages`] || []
    },
    /**
     * O interruptor, como COMPUTED — e não a constante lida direto no
     * `mostrarCartaoDePerfil`. É o que permite ao teste exercitar os DOIS
     * sentidos da guarda com o cartão desligado em produção (mesma receita de
     * `falaDoPrimeiroContatoLigada`, no mixin do avatar), e é onde uma decisão
     * do servidor (funcionalidade do escritório) entraria sem mexer na tela.
     */
    cartaoDePerfilLigado() {
      return CARTAO_DE_PERFIL_LIGADO
    },
    mostrarCartaoDePerfil() {
      if (!this.cartaoDePerfilLigado) return false
      return Boolean(this.propostaDePerfil)
    },
    /**
     * ★ A casa acabou de PEDIR a apresentação?
     *
     * O roteiro da boas-vindas termina em *"agora fale um pouco sobre você e
     * seu objetivo com a plataforma"* (`roteiro.js::FECHAMENTO`), e a conversa
     * com o avatar é a porta falada dessa mesma pergunta. Enquanto uma das
     * duas está na tela, o turno seguinte é RESPOSTA a um pedido de
     * apresentação — e só aí um objetivo aberto por "quero…" vale sozinho.
     *
     * ⚠️ **Fora disso a régua é mais dura de propósito**, e o motivo é este
     * mixin: ele avalia TODO turno de TODA conversa, não só o primeiro
     * contato. Sem a distinção, "quero ver o PL do Ilo" no meio do trabalho
     * viraria uma proposta de gravar "ver o PL do Ilo" como objetivo de
     * carreira da pessoa — que é a família do cartão que o owner recebeu em
     * 18/09 ("O que preciso fazer hoje?" → `objetivo: "fazer hoje"`).
     *
     * ★ **[18/09/2026] `boasVindasNoTurno` entra junto, e não é redundância.**
     * Quando o backend resolve o primeiro contato como TURNO próprio, a
     * apresentação passa a ser o CARTÃO da resposta e `mostrarBoasVindas` cai
     * para `false` (é a exclusão mútua de `view-chat-boas-vindas.mixin.js`).
     * Sem este terceiro campo, a pergunta "a casa acabou de pedir?" passaria a
     * responder NÃO exatamente no turno em que a casa pediu — e a régua dura
     * voltaria a valer contra a resposta que ela existe para acolher.
     *
     * Os três campos vêm de mixins irmãos na mesma view; `Boolean` os tolera
     * ausentes, que é como este mixin fica montável sozinho.
     */
    apresentacaoFoiPedida() {
      if (this.mostrarBoasVindas) return true
      if (this.boasVindasNoTurno) return true
      return Boolean(this.conversaComAvatarAtiva)
    },
    /**
     * A APRESENTAÇÃO está conduzindo a conversa agora?
     *
     * ★ Enquanto ela conduz, ela é a dona de perguntar quem a pessoa é — a
     * etapa 1 do onboarding faz exatamente isso, com o formulário. Os dois
     * juntos pedem o mesmo dado em dois formatos na mesma tela, e o cartão de
     * perfil ainda chega com metade dos campos vazios, porque extrai de uma
     * resposta escrita para outro lugar (decisão do owner, 18/09/2026:
     * "assume e suprime").
     *
     * ⚠️ A régua é a ÚLTIMA mensagem do assistente, e não "a conversa já teve
     * onboarding": terminado o roteiro, o cartão volta a valer.
     */
    onboardingConduzindo() {
      return onboardingConduzindo(this.mensagensDaConversa, ehCartaoDeBoasVindas)
    },
  },
  watch: {
    mensagensDaConversa: 'avaliarApresentacaoDoTurno',
  },
  methods: {
    /**
     * A última mensagem do profissional virou uma apresentação?
     *
     * Roda a cada mudança da conversa e é barata: `extrairApresentacao`
     * devolve `null` para qualquer texto sem marcador de apresentação, que é
     * a esmagadora maioria das mensagens — e também para pergunta, fragmento e
     * pedido, que é o que sobra depois das réguas de 18/09.
     *
     * ★ **O turno lido é o ÚLTIMO do profissional, e era o certo.** No cartão
     * que o owner reportou, a última mensagem dele de fato era a pergunta; a
     * apresentação ainda estava no composer, não enviada. O que falhou não foi
     * QUAL turno se lê, foi o que conta como apresentação.
     */
    avaliarApresentacaoDoTurno() {
      if (this.perfilJaTratadoNestaTela || this.salvandoPerfil) return
      // Ver `onboardingConduzindo`: enquanto a apresentação conduz, ela é a
      // dona de perguntar quem a pessoa é.
      if (this.onboardingConduzindo) return
      const texto = this.ultimoTextoDoProfissional()
      if (!texto || texto === this.textoDaPropostaCorrente) return

      const usuario = (this.$currentUser && this.$currentUser()) || {}
      const proposta = extrairApresentacao(texto, {
        nomeDaSessao: usuario.nome,
        apresentacaoPedida: this.apresentacaoFoiPedida,
      })
      if (!proposta) return

      this.propostaDePerfil = proposta
      this.textoDaPropostaCorrente = texto
      this.erroDoPerfil = ''
    },
    ultimoTextoDoProfissional() {
      const mensagens = this.mensagensDaConversa
      for (let i = mensagens.length - 1; i >= 0; i -= 1) {
        const m = mensagens[i]
        if (m && m.role === 'user') return String(m.content || '')
      }
      return ''
    },
    /**
     * O clique em "Salvar na memória" — o ÚNICO caminho de gravação.
     *
     * ⚠️ **Lê o perfil antes de gravar, e desiste se a leitura falhar.** O
     * `PUT /memoria/perfil` SUBSTITUI o dict `extras` inteiro
     * (`usecase_memoria.perfil_set`, `substituir_extras=True`, medido no
     * backend): gravar sem saber o que já existe apagaria em silêncio tudo o
     * que o profissional tinha guardado antes. Não há como fazer merge sem
     * ler, e não há razão para arriscar o que já é dele.
     */
    async confirmarPerfil(valores) {
      if (this.salvandoPerfil) return
      const extrasDaProposta = extrasDaApresentacao(valores)
      if (!Object.keys(extrasDaProposta).length) return

      this.salvandoPerfil = true
      this.erroDoPerfil = ''
      try {
        await this.gravarExtrasDoPerfil(extrasDaProposta)
      } finally {
        this.salvandoPerfil = false
      }
    },
    /**
     * A gravação em si. Separada de `confirmarPerfil` só para caber no teto de
     * statements do ESLint — a trava (`salvandoPerfil`) e o `finally` ficam
     * lá, que é onde a reentrância se decide.
     */
    async gravarExtrasDoPerfil(extrasDaProposta) {
      const extrasAtuais = await this.lerExtrasAtuais()
      if (!extrasAtuais) {
        this.erroDoPerfil = CONFIRMAR_PERFIL.ERRO_LER_PERFIL
        return
      }
      const r = await apiMemoria.salvarPerfil({
        extras: { ...extrasAtuais, ...extrasDaProposta },
      })
      if (!r || r.status !== 200) {
        this.erroDoPerfil = (r && r.message) || CONFIRMAR_PERFIL.ERRO_SALVAR
        return
      }
      this.perfilJaTratadoNestaTela = true
      this.propostaDePerfil = null
      this.$showSnack({ text: CONFIRMAR_PERFIL.SALVO, color: 'success', button: true })
    },
    /**
     * Os `extras` que já estão gravados, ou `null` quando não deu para ler.
     *
     * `null` é diferente de `{}`: o primeiro é "não sei", e com ele não se
     * grava; o segundo é "sei que está vazio", e com ele se grava.
     */
    async lerExtrasAtuais() {
      const r = await apiMemoria.listar()
      if (!r || r.status !== 200 || !r.value) return null
      const perfil = r.value.perfil || {}
      return perfil.extras || {}
    },
    /** "Agora não" — nada é gravado, e a proposta não volta nesta passagem. */
    dispensarPerfil() {
      this.propostaDePerfil = null
      this.erroDoPerfil = ''
      this.perfilJaTratadoNestaTela = true
    },
    /**
     * A boas-vindas terminou: o modo de voz fica pronto para a resposta
     * (AC-3.1).
     *
     * ★ **Ponto de entrada PÚBLICO, chamado de fora.** Quem monta a
     * boas-vindas (`components/boas-vindas-falada/`, US-2) liga `@fim` aqui —
     * é o gancho que o próprio componente declara emitir. Enquanto a US-2 não
     * o montar, este método existe e não é chamado: nada muda na tela.
     *
     * ⚠️ **A falha ao abrir o microfone não pode derrubar nada.** Permissão
     * negada, dispositivo ausente ou navegador sem suporte são desfechos
     * normais — e em todos eles o composer continua aceitando texto, que é
     * metade do AC-3.1. Por isso o `try`: a voz é uma oferta, não um pedágio.
     */
    aoFimDaBoasVindas() {
      // ★ **[18/09/2026] Aqui NÃO se abre o modo de voz — a conversa falada do
      // primeiro contato é com o AVATAR.** Direção do owner: *"nesse primeiro
      // chat, a conversa por voz seja com o avatar, nao com o modo voz (mas só
      // entrada ser diferente, o fluxo e racional permanece)"*.
      //
      // Até esta data, este gancho chamava `composer.iniciarVoz()`. Duas
      // consequências, e a segunda é a que decidiu:
      //
      // 1. desde a D-O-4 o Modo de Voz é o transporte em TEMPO REAL, então o
      //    primeiro contato abria uma sessão **paga por minuto** sozinho, sem
      //    ninguém ter pedido;
      // 2. naquele canal a IA **não consegue falar a resposta**: o provedor
      //    descarta o que mandamos com a delegação aberta (26 appends, 0 ecos
      //    — [T071]) e não verbaliza nem o desfecho (`promessa-exec75`, 18/09:
      //    `response.item.create` 1, **0 deltas de fala depois**). A pessoa
      //    ganhava um microfone aberto e um silêncio.
      //
      // O avatar fala pelo TTS de produção num `<audio>` nosso, fora do data
      // channel — e a resposta dela entra por texto ou por DITADO, que é a
      // transcrição que o composer já tem. "Só a entrada é diferente."
      if (typeof this.iniciarConversaComAvatar === 'function') {
        this.iniciarConversaComAvatar()
      }
    },
  },
}
